import { type CollisionDetection, closestCenter, type Modifier } from '@dnd-kit/core';
import { type useSortable } from '@dnd-kit/sortable';
import { getEventCoordinates } from '@dnd-kit/utilities';
import { type CSSProperties, type RefObject } from 'react';

import { SortDirection } from '@air/api/types';

import { LOCAL_STORAGE_BASE_KEY } from '~/constants/localStorageKeys';
import { type ConfigViewOption } from '~/store/configViews/types';

const FIELD_ORDER_KEY = `${LOCAL_STORAGE_BASE_KEY}/currentConfigurableViewHiddenFieldsOrder`;
const getLocalStorageKey = (boardId: string) => `${FIELD_ORDER_KEY}/${boardId}`;

export const getCurrentConfigurableViewHiddenFieldsOrder = (boardId: string): string[] | null => {
  try {
    const savedOrder = localStorage.getItem(getLocalStorageKey(boardId));
    return savedOrder ? JSON.parse(savedOrder) : null;
  } catch (e) {
    console.error('Failed to parse saved field order:', e);
    return null;
  }
};

export const setCurrentConfigurableViewHiddenFieldsOrder = (boardId: string, order: string[]): void => {
  localStorage.setItem(getLocalStorageKey(boardId), JSON.stringify(order));
};

export const visibleHeader: ConfigViewOption = {
  derivedId: 'visible-header',
  fieldName: 'visible-header',
  label: 'Visible',
  isVisible: true,
  supports: [],
  isCurrentSortedOption: false,
  sortDirection: SortDirection.asc,
  isCustomField: false,
};

export const hiddenHeader: ConfigViewOption = {
  derivedId: 'hidden-header',
  fieldName: 'hidden-header',
  label: 'Hidden',
  isVisible: false,
  supports: [],
  isCurrentSortedOption: false,
  sortDirection: SortDirection.asc,
  isCustomField: false,
};

export const excludeHeaderOptions = (option: ConfigViewOption) =>
  option.derivedId !== visibleHeader.derivedId && option.derivedId !== hiddenHeader.derivedId;

const WIDTH_OFFSET = 3;

type SortableProps = ReturnType<typeof useSortable>;

export const getSortableItemStyles = (sortableProps: SortableProps): CSSProperties | undefined => {
  return sortableProps
    ? {
        transform: sortableProps.transform
          ? `translate3d(${sortableProps.transform.x}px, ${sortableProps.transform.y}px, 0)`
          : undefined,
        transition: sortableProps.transition,
        ...(sortableProps.isDragging && {
          width: `calc(100% + 0.75rem + ${WIDTH_OFFSET}px)`,
          paddingLeft: `calc(0.375rem + ${WIDTH_OFFSET / 2}px)`,
          paddingRight: `calc(0.75rem + ${WIDTH_OFFSET / 2}px)`,
          marginLeft: `calc(-0.375rem - ${WIDTH_OFFSET / 2}px)`,
          boxShadow: '0px 9px 24px 0px rgba(0, 0, 0, 0.20)',
          backgroundColor: 'var(--colors-gray-1)',
          zIndex: 1000,
        }),
      }
    : undefined;
};

export const assignSortOrder = (options: ConfigViewOption[]) => {
  // performs in-place
  for (let i = 0; i < options.length; i++) {
    options[i].sortOrder = i + 1;
  }
};

export const sortBySortOrder = (a: ConfigViewOption, b: ConfigViewOption) =>
  (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER);

// Drag and drop modifiers

export const snapItemToCursor =
  (containerRef: RefObject<HTMLElement>): Modifier =>
  ({ activatorEvent, draggingNodeRect, transform }) => {
    if (draggingNodeRect && activatorEvent) {
      const activatorCoordinates = getEventCoordinates(activatorEvent);
      if (!activatorCoordinates) {
        return transform;
      }

      const container = containerRef.current;
      if (!container) {
        return transform;
      }
      const containerRect = container.getBoundingClientRect();

      // Calculate the initial offset from cursor to item corner
      const offsetY = activatorCoordinates.y - (draggingNodeRect.top + draggingNodeRect.height / 2);

      // Calculate the new position with boundaries
      const newY = Math.max(
        containerRect.top - draggingNodeRect.top,
        Math.min(containerRect.bottom - draggingNodeRect.bottom, transform.y + offsetY),
      );

      return {
        ...transform,
        y: newY,
      };
    }

    return transform;
  };

export const collisionDetection: CollisionDetection = (args) => {
  const collisions = closestCenter(args);

  // Prevent dropping above the visible-header (index 0)
  return collisions.filter((collision) => collision.id !== visibleHeader.derivedId);
};
